import * as React from 'react';
import { SimpleListing } from '@th/shared/src/types/SimpleListing';
import formatRepliersImageSource from '@th/shared/src/helpers/formatRepliersImageSource';
import styles from './ListingImageHero.module.scss';
import Button from '../Button';
import UnauthenticatedCarouselOverlay from './UnauthenticatedCarouselOverlay';
import { twMerge } from 'tailwind-merge';
import { DEFAULT_IMAGE_RESOLUTION } from '@th/frontend-shared/src/constants/images';
import ListingInfoBadge from './ListingInfoBadge';

type Props = {
  listing: SimpleListing;
  onImageClick: () => void;
  isBlurred?: boolean;
};

function ShowAllPhotosButton({ onImageClick }) {
  return (
    <div className="absolute bottom-8 right-8">
      <Button
        variant={'solid'}
        color={'white'}
        onClick={onImageClick}
        title={'Show all photos'}
        buttonClassName="border-2 border-black "
        textClassName="text-sm lg:text-lg text-text truncate"
      />
    </div>
  );
}

export default function ListingImageHero({
  listing,
  onImageClick,
  isBlurred,
}: Props) {
  const { images } = listing;

  const imageUrls = images.slice(0, 5).map(image => {
    return formatRepliersImageSource(image, 'medium');
  });

  if (imageUrls.length === 0) {
    return (
      <div className="relative flex justify-center">
        <img
          style={{ maxHeight: '36em' }}
          src={'/images/imagePlaceholder.png'}
          alt={`Preview image for ${listing.address.formattedAddressShortOneLiner()}`}
          className={twMerge(
            'object-cover align-bottom aspect-3/2 rounded-2xl',
            isBlurred && 'filter blur-sm',
          )}
          key={'no-image'}
        />
        <ListingInfoBadge listing={listing} />
        {isBlurred && <UnauthenticatedCarouselOverlay small={false} />}
      </div>
    );
  }

  if (imageUrls.length === 1) {
    return (
      <div className="relative flex justify-center">
        <img
          onClick={onImageClick}
          style={{ maxHeight: '36em' }}
          src={imageUrls[0].uri}
          alt={`Preview image for ${listing.address.formattedAddressShortOneLiner()}`}
          className={twMerge(
            'object-cover align-bottom aspect-3/2 rounded-2xl cursor-pointer',
            isBlurred && 'filter blur-sm',
          )}
          key={imageUrls[0].uri}
        />
        <ListingInfoBadge listing={listing} />
        {isBlurred && <UnauthenticatedCarouselOverlay small={false} />}
      </div>
    );
  }

  return (
    <div className="relative flex justify-center">
      <div
        className={twMerge(
          styles.container,
          'grid grid-cols-4 grid-rows-2 gap-1 lg:gap-3 min-w-full',
          isBlurred && 'filter blur-sm',
        )}
      >
        {imageUrls.length < 5 ? (
          <>
            <img
              onClick={onImageClick}
              src={imageUrls[0].uri}
              alt={`Preview image for ${listing.address.formattedAddressShortOneLiner()}`}
              className="object-cover align-bottom h-full w-full rounded-l-2xl col-span-2 row-span-2 hover:brightness-75 cursor-pointer"
              key={imageUrls[0].uri}
            />
            <div className="col-span-2 row-span-2">
              <img
                onClick={onImageClick}
                src={imageUrls[1].uri}
                alt={`Preview image for ${listing.address.formattedAddressShortOneLiner()}`}
                className="object-cover align-bottom h-full w-full rounded-r-2xl hover:brightness-75 cursor-pointer"
                key={imageUrls[1].uri}
              />
              <ShowAllPhotosButton onImageClick={onImageClick} />
            </div>
          </>
        ) : (
          <>
            <img
              onClick={onImageClick}
              src={imageUrls[0].uri}
              alt={`Preview image for ${listing.address.formattedAddressShortOneLiner()}`}
              className="object-cover align-bottom h-full w-full rounded-l-2xl col-span-2 row-span-2 hover:brightness-75 cursor-pointer"
              key={imageUrls[0].uri}
            />
            <img
              onClick={onImageClick}
              src={imageUrls[1].uri}
              alt={`Preview image for ${listing.address.formattedAddressShortOneLiner()}`}
              className="object-cover align-bottom h-full w-full hover:brightness-75 cursor-pointer"
              key={imageUrls[1].uri}
            />
            <img
              onClick={onImageClick}
              src={imageUrls[2].uri}
              alt={`Preview image for ${listing.address.formattedAddressShortOneLiner()}`}
              className="object-cover align-bottom h-full w-full rounded-tr-2xl hover:brightness-75 cursor-pointer"
              key={imageUrls[2].uri}
            />
            <img
              onClick={onImageClick}
              src={imageUrls[3].uri}
              alt={`Preview image for ${listing.address.formattedAddressShortOneLiner()}`}
              className="object-cover align-bottom h-full w-full hover:brightness-75 cursor-pointer"
              key={imageUrls[3].uri}
            />
            <div className="relative">
              <img
                onClick={onImageClick}
                src={imageUrls[4].uri}
                alt={`Preview image for ${listing.address.formattedAddressShortOneLiner()}`}
                className="object-cover align-bottom h-full w-full rounded-br-2xl hover:brightness-75 cursor-pointer"
                key={imageUrls[4].uri}
              />
              <ShowAllPhotosButton onImageClick={onImageClick} />
            </div>
          </>
        )}
      </div>
      <ListingInfoBadge listing={listing} />
      {isBlurred && <UnauthenticatedCarouselOverlay small={false} />}
    </div>
  );
}
